import React, { useEffect, useState } from "react";
import { Add, DeleteOutline } from "@mui/icons-material";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { AssetCustomFieldsAPI } from "../api/assetCustomFields";

const emptyForm = { label: "", field_type: "TEXT", options: "" };

export default function AssetCustomFieldsDialog({ open, onClose, onChanged }) {
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const response = await AssetCustomFieldsAPI.list();
      setFields(response.data?.data || []);
    } catch (err) { setError(err.response?.data?.error || "Unable to load custom fields."); }
  };
  useEffect(() => { if (open) { setError(""); load(); } }, [open]);

  const addField = async () => {
    setSaving(true); setError("");
    try {
      await AssetCustomFieldsAPI.create(form);
      setForm(emptyForm);
      await load();
      onChanged?.();
    } catch (err) { setError(err.response?.data?.error || "Unable to add custom field."); }
    finally { setSaving(false); }
  };
  const removeField = async (field) => {
    if (!window.confirm(`Remove “${field.label}” from new asset forms and templates? Existing asset values will be retained.`)) return;
    try { await AssetCustomFieldsAPI.deactivate(field.id); await load(); onChanged?.(); }
    catch (err) { setError(err.response?.data?.error || "Unable to remove custom field."); }
  };

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 800 }}>Asset custom fields</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" mb={2}>Add reusable fields for every asset. New fields are also included in the bulk-upload template.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={1} divider={<Divider flexItem />} sx={{ mb: 3 }}>
        {fields.length ? fields.map((field) => <Box key={field.id} display="flex" alignItems="center" justifyContent="space-between" gap={1} py={0.5}>
          <Box><Typography fontWeight={700}>{field.label}</Typography><Typography variant="caption" color="text.secondary">{field.field_type === "DROPDOWN" ? `Dropdown: ${(field.options || []).join(", ")}` : "Text"}</Typography></Box>
          <IconButton color="error" size="small" aria-label={`Remove ${field.label}`} onClick={() => removeField(field)}><DeleteOutline fontSize="small" /></IconButton>
        </Box>) : <Typography variant="body2" color="text.secondary">No custom fields yet.</Typography>}
      </Stack>
      <Box sx={{ p: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2 }}>
        <Typography fontWeight={800} mb={1.5}>Add a field</Typography>
        <Stack spacing={1.5}>
          <TextField label="Field label" size="small" value={form.label} onChange={(e) => setForm((value) => ({ ...value, label: e.target.value }))} placeholder="e.g. Cost centre" inputProps={{ maxLength: 100 }} />
          <FormControl size="small"><InputLabel>Field type</InputLabel><Select label="Field type" value={form.field_type} onChange={(e) => setForm((value) => ({ ...value, field_type: e.target.value }))}><MenuItem value="TEXT">Text</MenuItem><MenuItem value="DROPDOWN">Dropdown</MenuItem></Select></FormControl>
          {form.field_type === "DROPDOWN" && <TextField label="Options" size="small" value={form.options} onChange={(e) => setForm((value) => ({ ...value, options: e.target.value }))} placeholder="e.g. Finance, IT, Operations" helperText="Separate options with commas." />}
          <Button variant="contained" startIcon={<Add />} disabled={saving || !form.label.trim() || (form.field_type === "DROPDOWN" && !form.options.trim())} onClick={addField}>Add field</Button>
        </Stack>
      </Box>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
